/**
 * Hook for managing inquiry filter state
 * Handles status, product, search, and pagination filters with localStorage persistence
 */

import { useState, useEffect } from 'react';
import { InquiryFilters, InquiryStatus } from '@/modules/marketplace/types/inquiries.types';

const FILTERS_STORAGE_KEY = 'inquiry_filters';

/**
 * Default filter values
 */
const DEFAULT_FILTERS: InquiryFilters = {
  status: 'ALL',
  productId: undefined,
  search: undefined,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 0,
  pageSize: 20,
};

/**
 * Hook to manage inquiry filters with localStorage persistence
 */
export function useInquiryFilters() {
  const [filters, setFilters] = useState<InquiryFilters>(() => {
    // Load from localStorage on initial mount
    try {
      const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Reset page to 0 on load
        return { ...parsed, page: 0 };
      }
    } catch (e) {
      console.error('Failed to parse stored filters:', e);
    }
    return DEFAULT_FILTERS;
  });

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  /**
   * Update status filter
   */
  const setStatus = (status: 'ALL' | InquiryStatus) => {
    setFilters((prev) => ({ ...prev, status, page: 0 }));
  };

  /**
   * Update product filter
   */
  const setProductId = (productId: string | undefined) => {
    setFilters((prev) => ({ ...prev, productId, page: 0 }));
  };

  /**
   * Update search filter
   */
  const setSearch = (search: string | undefined) => {
    setFilters((prev) => ({ ...prev, search, page: 0 }));
  };

  /**
   * Update pagination
   */
  const setPage = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  /**
   * Update page size
   */
  const setPageSize = (pageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize, page: 0 }));
  };

  /**
   * Update sorting
   */
  const setSorting = (sortBy: 'createdAt' | 'status' | 'customerName', sortOrder: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sortBy, sortOrder }));
  };

  /**
   * Reset all filters to defaults
   */
  const reset = () => {
    setFilters(DEFAULT_FILTERS);
  };

  /**
   * Clear specific filters
   */
  const clearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      status: 'ALL',
      productId: undefined,
      search: undefined,
      page: 0,
    }));
  };

  return {
    filters,
    setStatus,
    setProductId,
    setSearch,
    setPage,
    setPageSize,
    setSorting,
    reset,
    clearFilters,
  };
}

/**
 * Status options for filter dropdown
 */
export const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Statuses' },
  { value: InquiryStatus.NEW, label: 'New' },
  { value: InquiryStatus.CONTACTED, label: 'Contacted' },
  { value: InquiryStatus.CLOSED, label: 'Closed' },
] as const;

/**
 * Page size options
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
